"use client";

import React, { useEffect, useState } from "react";
import { MessageSquareQuote, Star } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface TestimonialRecord {
  id: number;
  name?: string;
  rating?: number | string;
  comment?: string;
  [key: string]: any;
}
export default function CustomerTestimonials() {
  const [items, setItems] = useState<TestimonialRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let isMounted = true;
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setLoading(false);
      return;
    }
    client.pluginRequest("wvc-custom-records/v1/collections/testimonials/records", {
      params: {
        per_page: 12,
        sort: "-created_at"
      }
    }).then((res: any) => {
      if (!isMounted) return;
      if (res && Array.isArray(res.items)) {
        setItems(res.items);
      } else {
        setItems([]);
      }
      setLoading(false);
    }).catch((err: any) => {
      if (!isMounted) return;
      setError(err?.message || "تعذر تحميل التقييمات حالياً");
      setLoading(false);
    });
    return () => {
      isMounted = false;
    };
  }, []);
  return <section id="customertestimonials" data-nav="light" dir="rtl" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground mb-3 font-default">
            آراء وتجارب العملاء
          </h1>
          <p className="text-muted-foreground text-base md:text-lg">
            نعتز برضا عملائنا في الرياض عن خدمات تنظيف وصيانة الأفران
          </p>
        </div>

        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({
          length: 3
        }).map((_, i) => <Card key={i} data-index={i} className="border border-border bg-card rounded-lg shadow-sm">
                <CardHeader className="space-y-2">
                  <div className="flex items-center gap-1">
                    {Array.from({
                length: 5
              }).map((_, starIdx) => <Skeleton key={starIdx} className="size-4 rounded-full" />)}
                  </div>
                  <Skeleton className="h-5 w-1/2 rounded" />
                </CardHeader>
                <CardContent className="space-y-2">
                  <Skeleton className="h-4 w-full rounded" />
                  <Skeleton className="h-4 w-4/5 rounded" />
                </CardContent>
              </Card>)}
          </div> : error ? <div className="text-center py-12 px-4 rounded-lg border border-border bg-muted/40 max-w-md mx-auto">
            <p className="text-muted-foreground text-sm">{error}</p>
          </div> : items.length === 0 ? <div className="text-center py-16 px-4 rounded-lg border border-border bg-card max-w-md mx-auto shadow-sm">
            <MessageSquareQuote className="size-10 text-muted-foreground mx-auto mb-3 opacity-60" />
            <h3 className="text-base font-medium text-foreground mb-1">
              لا توجد تقييمات منشورة حتى الآن
            </h3>
            <p className="text-muted-foreground text-sm">
              سيتم عرض تجارب وآراء العملاء المعتمدة هنا فور إضافتها.
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item, i) => {
          const numericRating = Math.max(0, Math.min(5, Math.round(Number(item.rating) || 5)));
          return <Card key={item.id} data-index={i} className="border border-border bg-card text-card-foreground rounded-lg shadow-sm flex flex-col justify-between">
                  <CardHeader className="gap-2">
                    <div className="flex items-center gap-1 text-primary" aria-label={`التقييم: ${numericRating} من 5`}>
                      {Array.from({
                  length: 5
                }).map((_, starIndex) => {
                  const isFilled = starIndex < numericRating;
                  return <Star key={starIndex} className={`size-4 ${isFilled ? "fill-primary text-primary" : "text-muted-foreground/30 stroke-muted-foreground/40"}`} />;
                })}
                      <span className="text-xs text-muted-foreground mr-1.5 font-medium">
                        {numericRating} / 5
                      </span>
                    </div>

                    {item.name ? <CardTitle className="text-base font-semibold text-foreground">
                        {item.name}
                      </CardTitle> : null}
                  </CardHeader>

                  {item.comment ? <CardContent>
                      <p className="text-muted-foreground text-sm leading-relaxed whitespace-pre-line">
                        {item.comment}
                      </p>
                    </CardContent> : null}
                </Card>;
        })}
          </div>}
      </div>
    </section>;
}